import Fuse, { FuseResult } from 'fuse.js';
import { sortByDate } from 'src/components/Table/sorting';
import { IServiceRequest } from 'src/services/api/ServiceRequestDashboard/apiContracts';

interface IBaseServiceRequest extends IServiceRequest {
  tarionItemNumber?: string | null;
}

interface IProcessedServiceRequest extends IBaseServiceRequest {
  name: string;
  isGroup: boolean;
}

interface IServiceRequestChild extends IProcessedServiceRequest {
  isGroup: false;
  groupId: string;
  groupName: string;
}

interface IServiceRequestGroup extends IProcessedServiceRequest {
  isGroup: true;
  children: IServiceRequestChild[];
  groupId: string;
  groupName: string;
}

interface IServiceRequestStandalone extends IProcessedServiceRequest {
  isGroup: false;
  groupId: string | null;
  groupName: string | null;
  children?: never;
}

interface IGroupingAccumulator {
  serviceRequest: IServiceRequestStandalone[];
  groups: IServiceRequestGroup[];
}

const hasTarionNumbers = (serviceRequests: IBaseServiceRequest[]): boolean => {
  return serviceRequests.some(
    (request: IBaseServiceRequest) => request.tarionItemNumber !== null
  );
};

const normalizeTarionItemNumber = (
  tarionItemNumber: string | null,
  shouldNormalize: boolean
): string | null => {
  if (!shouldNormalize) return tarionItemNumber;
  return tarionItemNumber || 'N/A';
};

export const searchServiceRequests = (
  serviceRequests: IServiceRequest[],
  searchString: string
): IServiceRequest[] => {
  const fuse = new Fuse(serviceRequests, {
    minMatchCharLength: 2,
    threshold: 0.3,
    keys: [
      'number',
      'description',
      'area',
      'item',
      'type',
      'status',
      'ticketNumber',
    ],
  });

  if (searchString.length < 3) return serviceRequests;
  return fuse
    .search(searchString)
    .map(
      (result: FuseResult<IServiceRequest>) => result.item as IServiceRequest
    );
};

const sortGroupChildrenByTarionNumber = (
  groups: IServiceRequestGroup[]
): IServiceRequestGroup[] => {
  const sortedGroup: IServiceRequestGroup[] = groups.map(
    (group: IServiceRequestGroup) => {
      if (group.children && group.children.length > 1) {
        const unsortedChildren: IServiceRequestChild[] = group.children;

        const sortedChildren: IServiceRequestChild[] = [
          ...unsortedChildren,
        ].sort((a: IServiceRequestChild, b: IServiceRequestChild) => {
          const aTarionNumber: string = a.tarionItemNumber || '';
          const bTarionNumber: string = b.tarionItemNumber || '';

          return aTarionNumber.localeCompare(bTarionNumber, undefined, {
            numeric: true,
            sensitivity: 'base',
          });
        });

        return {
          ...group,
          children: sortedChildren,
        };
      }

      return group;
    }
  );

  return sortedGroup;
};

const groupServiceRequests = (serviceRequests: IServiceRequest[]) => {
  const shouldNormalizeTarion: boolean = hasTarionNumbers(serviceRequests);

  const { serviceRequest, groups } = serviceRequests.reduce(
    (acc: IGroupingAccumulator, serviceRequest: IBaseServiceRequest) => {
      if (serviceRequest.groupId) {
        const group: IServiceRequestGroup = acc.groups.find(
          (group: IServiceRequestGroup) => group.id === serviceRequest.groupId
        );

        const processedServiceRequest: IBaseServiceRequest = {
          ...serviceRequest,
          tarionItemNumber: normalizeTarionItemNumber(
            serviceRequest.tarionItemNumber,
            shouldNormalizeTarion
          ),
        };

        if (group) {
          const childItem: IServiceRequestChild = {
            ...processedServiceRequest,
            name: serviceRequest.number,
            isGroup: false,
          };

          group.children.push(childItem);
        } else {
          const newGroup: IServiceRequestGroup = {
            ...processedServiceRequest,
            id: serviceRequest.groupId,
            name: serviceRequest.groupName,
            children: [
              {
                ...processedServiceRequest,
                name: serviceRequest.number,
                isGroup: false,
              },
            ],
            isGroup: true,
            tarionItemNumber: null, // We have to set the group's tarionItemNumber to null, since groups don't have tarion numbers.
          };

          acc.groups.push(newGroup);
        }
      } else {
        const standaloneItem: IServiceRequestStandalone = {
          ...serviceRequest,
          tarionItemNumber: normalizeTarionItemNumber(
            serviceRequest.tarionItemNumber,
            shouldNormalizeTarion
          ),
          name: serviceRequest.number,
          isGroup: false,
        };

        acc.serviceRequest.push(standaloneItem);
      }
      return acc;
    },
    { serviceRequest: [], groups: [] }
  );

  const sortedGroups: IServiceRequestGroup[] =
    sortGroupChildrenByTarionNumber(groups);

  return [...serviceRequest, ...sortedGroups];
};

export const filterServiceRequests = (
  serviceRequests: IServiceRequest[],
  { searchString, pagination }
): IServiceRequest[] => {
  if (Array.isArray(serviceRequests) === false) return [];
  let filteredServiceRequests: IServiceRequest[] = serviceRequests;

  filteredServiceRequests =
    searchString.length > 2
      ? (searchServiceRequests(
          filteredServiceRequests,
          searchString
        ) as IServiceRequest[])
      : serviceRequests;

  filteredServiceRequests = groupServiceRequests(filteredServiceRequests);

  filteredServiceRequests.sort((a: any, b: any) =>
    sortByDate(b.createdDate, a.createdDate)
  );
  return filteredServiceRequests.slice(0, pagination);
};
